import { S3Object } from '../../../drive/types';
import { UseS3ServiceReturn } from '../../../drive/useS3Service';
import {
  buildNewKey,
  ensureTrailingSlash,
  generateUniqueName,
  getDisplayName,
  isDirectory,
  removeFileExtension,
} from '../helpers';
import { useModalState } from './useModalState';

export interface UseRenameOperationsProps {
  s3Service: UseS3ServiceReturn;
  currentDirectoryPath: string;
}

export interface RenameItem {
  key: string;
  name: string;
  isDirectory: boolean;
}

export const useRenameOperations = ({ s3Service, currentDirectoryPath }: UseRenameOperationsProps) => {
  const modalState = useModalState<RenameItem>();

  const handleRenameRequest = (item: S3Object) => {
    if (!item.Key) return;

    const isItemDirectory = isDirectory(item.Key);
    const displayName = getDisplayName(item.Key);
    const nameWithoutExtension = isItemDirectory ? displayName : removeFileExtension(displayName);

    modalState.open({
      key: item.Key,
      name: nameWithoutExtension,
      isDirectory: isItemDirectory,
    });
  };

  const handleRenameModalClose = modalState.close;

  const handleRenameModalConfirm = async (newName: string) => {
    if (!modalState.data) return;

    try {
      const { key: oldKey, isDirectory } = modalState.data;
      const newKey = buildNewKey(oldKey, newName, isDirectory);

      await s3Service.copyFile(oldKey, newKey);
      await s3Service.deleteFile(oldKey);
      const prefix = currentDirectoryPath ? ensureTrailingSlash(currentDirectoryPath) : undefined;
      await s3Service.listObjects(prefix);
    } catch (error) {
      console.error('Failed to rename item:', error);
    } finally {
      handleRenameModalClose();
    }
  };

  const generateUniqueNameForItem = (existingNames: string[]): string => {
    if (!modalState.data) return '';

    const { name: baseName, isDirectory, key } = modalState.data;

    if (isDirectory) {
      return generateUniqueName(baseName, existingNames);
    }

    // For files, we need to get the extension from the original key
    const extension = key.split('.').pop();
    const fullBaseName = extension ? `${baseName}.${extension}` : baseName;

    return generateUniqueName(fullBaseName, existingNames);
  };

  return {
    renameModalOpen: modalState.isOpen,
    itemToRename: modalState.data,
    handleRenameRequest,
    handleRenameModalClose,
    handleRenameModalConfirm,
    generateUniqueName: generateUniqueNameForItem,
  };
};
